import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdminStudentMatchesRDL = () => {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debug, setDebug] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [dataSourceFilter, setDataSourceFilter] = useState('all'); // 'all', 'current', 'verified'
  const [searchTerm, setSearchTerm] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  const [stats, setStats] = useState({
    totalStudentsWithMatches: 0,
    totalMatches: 0
  });

  // Token generation state
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [showBulkTokens, setShowBulkTokens] = useState(false);
  const [tokenExpiryHours, setTokenExpiryHours] = useState(720); // 30 days default
  const [tokenGenerating, setTokenGenerating] = useState(false);
  const [tokenSuccess, setTokenSuccess] = useState(null);

  useEffect(() => {
    fetchStudentMatches();
  }, []);

  const fetchStudentMatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(`${API_URL}/api/student-matches-rdl?debug=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch student matches');
      }

      const data = await response.json();
      setStudents(data.students || []);
      setStats({
        totalStudentsWithMatches: data.total_students_with_matches || 0,
        totalMatches: data.total_matches || 0
      });
      if (data.debug) {
        setDebug(data.debug);
      }
    } catch (err) {
      console.error('Error fetching student matches:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentExpanded = (studentId) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Helper function to parse admin name from verified_by field
  const getAdminName = (verifiedBy) => {
    if (!verifiedBy) return 'Unknown';

    // Format: "Admin Name (Admin - CSV Upload)" or "Admin Name (email@example.com)"
    const match = verifiedBy.match(/^(.+?)\s*\(/);
    if (match && match[1]) {
      return match[1].trim();
    }

    return verifiedBy;
  };

  // Helper function to format admin upload batch info
  const formatBatchInfo = (adminUploadNotes) => {
    if (!adminUploadNotes) return '';

    // Extract batch ID from format: "Imported via admin CSV upload - Batch: admin_123_1705334400"
    const match = adminUploadNotes.match(/Batch:\s*(.+)/);
    if (match && match[1]) {
      return match[1].trim();
    }

    return adminUploadNotes;
  };

  // Helper function to get valid token
  const getValidToken = () => {
    return localStorage.getItem('token');
  };

  // Toggle student selection
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // Select/deselect all filtered students
  const toggleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.student_info.id)));
    }
  };

  // Generate individual token
  const generateIndividualToken = async (studentId) => {
    try {
      setTokenGenerating(true);
      const token = getValidToken();

      const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, {
        applicant_ids: [studentId],
        expires_in_hours: tokenExpiryHours
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setTokenSuccess(`Update token generated successfully for student ID ${studentId}`);
      setTimeout(() => setTokenSuccess(null), 5000);

    } catch (err) {
      console.error('Error generating token:', err);
      setError(`Failed to generate update token: ${err.response?.data?.detail || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTokenGenerating(false);
    }
  };

  // Generate bulk update tokens
  const generateBulkTokens = async () => {
    if (selectedStudents.size === 0) {
      alert('Please select at least one student to generate tokens.');
      return;
    }

    try {
      setTokenGenerating(true);
      const token = getValidToken();

      const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, {
        applicant_ids: Array.from(selectedStudents),
        expires_in_hours: tokenExpiryHours
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setTokenSuccess(`Update tokens generated successfully for ${selectedStudents.size} students`);
      setSelectedStudents(new Set()); // Clear selections
      setShowBulkTokens(false); // Hide bulk panel
      setTimeout(() => setTokenSuccess(null), 5000);

    } catch (err) {
      console.error('Error generating bulk tokens:', err);
      setError(`Failed to generate bulk tokens: ${err.response?.data?.detail || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTokenGenerating(false);
    }
  };

  // Basic CSV export without token data
  const exportBasicCSV = () => {
    // Determine which students to export (selected or all filtered)
    const studentsToExport = selectedStudents.size > 0
      ? filteredStudents.filter(s => selectedStudents.has(s.student_info.id))
      : filteredStudents;

    if (studentsToExport.length === 0) {
      alert('No student matches to export.');
      return;
    }

    // CSV headers
    const headers = [
      'Student ID',
      'Student Name',
      'Email',
      'University',
      'GPA',
      'Major',
      'Class Year',
      'Total Matches',
      'Matching Organizations'
    ];

    // Convert matches to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...studentsToExport.map(studentData => [
        studentData.student_info.id,
        `"${studentData.student_info.name}"`,
        studentData.student_info.email,
        `"${studentData.student_info.university}"`,
        studentData.student_info.gpa,
        `"${studentData.student_info.major}"`,
        `"${studentData.student_info.class_year}"`,
        studentData.match_count,
        `"${studentData.matching_organizations.map(org => org.organization).join('; ')}"`,
      ].join(','))
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `Student_Matches_RDL_${timestamp}.csv`);

      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // CSV Export functionality with token data
  const exportToCSV = async () => {
    // Determine which students to export (selected or all filtered)
    const studentsToExport = selectedStudents.size > 0
      ? filteredStudents.filter(s => selectedStudents.has(s.student_info.id))
      : filteredStudents;

    if (studentsToExport.length === 0) {
      alert('No student matches to export.');
      return;
    }

    try {
      // Show loading state
      setTokenGenerating(true);

      // Fetch token data for all students (handle pagination if needed)
      const token = getValidToken();
      let allTokens = [];
      let offset = 0;
      const limit = 1000;
      let hasMore = true;

      // Fetch tokens in batches if there are more than 1000
      while (hasMore) {
        const tokenResponse = await axios.get(
          `${BACKEND_URL}/api/list-update-tokens?active_only=false&limit=${limit}&offset=${offset}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        const batchTokens = tokenResponse.data.tokens || [];
        allTokens = [...allTokens, ...batchTokens];

        // Check if we've fetched all tokens
        if (batchTokens.length < limit || allTokens.length >= tokenResponse.data.total) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }

      // Create a map of applicant_id to token data
      const tokenMap = {};

      allTokens.forEach(tokenData => {
        // Each token has a single applicant_id
        const applicantId = tokenData.applicant_id;

        // Keep the most recent active token for each applicant
        const isActive = !tokenData.is_expired && !tokenData.used;

        // Only map if we don't have a token for this applicant yet, or if this is an active token
        if (!tokenMap[applicantId] || (isActive && (tokenMap[applicantId].is_expired || tokenMap[applicantId].used))) {
          tokenMap[applicantId] = {
            ...tokenData,
            status: isActive ? 'active' : 'inactive'
          };
        }
      });

      // Calculate hours remaining for each token
      const calculateHoursRemaining = (expiresAt) => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const hoursRemaining = (expiry - now) / (1000 * 60 * 60); // Convert ms to hours
        return hoursRemaining > 0 ? Math.round(hoursRemaining) : 0;
      };

      // CSV headers
      const headers = [
        'Student ID',
        'Student Name',
        'Email',
        'University',
        'GPA',
        'Major',
        'Class Year',
        'Total Matches',
        'Matching Organizations',
        'Update Token',
        'Token Hours Remaining'
      ];

      // Convert matches to CSV rows
      const csvRows = [
        headers.join(','), // Header row
        ...studentsToExport.map(studentData => {
          const studentId = studentData.student_info.id;
          const tokenData = tokenMap[studentId];
          const hasActiveToken = tokenData && tokenData.status === 'active';

          return [
            studentId,
            `"${studentData.student_info.name}"`,
            studentData.student_info.email,
            `"${studentData.student_info.university}"`,
            studentData.student_info.gpa,
            `"${studentData.student_info.major}"`,
            `"${studentData.student_info.class_year}"`,
            studentData.match_count,
            `"${studentData.matching_organizations.map(org => org.organization).join('; ')}"`,
            hasActiveToken ? tokenData.token : '',
            hasActiveToken ? calculateHoursRemaining(tokenData.expires_at) : ''
          ].join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 10);
        link.setAttribute('download', `Student_Matches_RDL_with_Tokens_${timestamp}.csv`);

        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting CSV with token data:', err);
      console.error('Error details:', err.response?.data);

      // If token fetch failed, export without token data
      if (err.response?.status === 404 || err.response?.status === 500) {
        const confirmExport = window.confirm('Unable to fetch token data. Export CSV without token information?');
        if (confirmExport) {
          exportBasicCSV();
        }
      } else {
        alert(`Failed to export CSV: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
      }
    } finally {
      setTokenGenerating(false);
    }
  };

  // Get unique universities for filter dropdown
  const getUniqueUniversities = () => {
    const universities = students.map(student => student.student_info.university);
    return [...new Set(universities)].sort();
  };

  // Filter students based on data source, search, and university
  const filteredStudents = students
    .filter(student => {
      // Data source filter
      if (dataSourceFilter !== 'all' && student.student_info.data_source !== dataSourceFilter) {
        return false;
      }

      // Search filter (name, email, or university)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = student.student_info.name.toLowerCase().includes(searchLower);
        const matchesEmail = student.student_info.email.toLowerCase().includes(searchLower);
        const matchesUniversity = student.student_info.university.toLowerCase().includes(searchLower);

        if (!matchesName && !matchesEmail && !matchesUniversity) {
          return false;
        }
      }

      // University filter
      if (universityFilter && !student.student_info.university.toLowerCase().includes(universityFilter.toLowerCase())) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      // Sort alphabetically by name
      const nameA = a.student_info.name.toLowerCase();
      const nameB = b.student_info.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // Calculate filtered stats
  const filteredStats = {
    totalStudentsWithMatches: filteredStudents.length,
    totalMatches: filteredStudents.reduce((sum, student) => sum + student.match_count, 0)
  };

  const StatCard = ({ label, value, color = 'blue', sublabel = null }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className={`text-3xl font-bold text-${color}-600 mb-1`}>
        {value.toLocaleString()}
      </div>
      <div className="text-sm text-gray-600">{label}</div>
      {sublabel && <div className="text-xs text-gray-400 mt-1">{sublabel}</div>}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading student matches...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Admin Dashboard
            </button>

            <button
              onClick={() => navigate('/admin/student-matches')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
            >
              Compare with Verified-Only View
            </button>
          </div>

          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">
              Student Matches
            </h1>
            <span className="px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
              Recruiter Dashboard Logic
            </span>
          </div>

          <p className="text-gray-600 mt-2">
            Shows what recruiters actually see using intelligent data source selection
          </p>

          <div className="mt-3 text-sm text-gray-500 bg-blue-50 p-3 rounded border border-blue-200">
            <p className="font-medium text-blue-900 mb-1">How This Works:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Uses most recent data (verified vs application) based on timestamps</li>
              <li>Falls back to self-reported when verified data is invalid ("0", "NA")</li>
              <li>Same logic as recruiter dashboard - shows real matching behavior</li>
            </ul>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
          {/* Data Source Filter */}
          <div>
            <span className="text-sm font-medium text-gray-700 mb-2 block">Filter by Data Source:</span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setDataSourceFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dataSourceFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Students
                <span className="ml-2 text-xs">({students.length})</span>
              </button>
              <button
                onClick={() => setDataSourceFilter('current')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dataSourceFilter === 'current'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📱 Application Data
                <span className="ml-2 text-xs">
                  ({students.filter(s => s.student_info.data_source === 'current').length})
                </span>
              </button>
              <button
                onClick={() => setDataSourceFilter('verified')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dataSourceFilter === 'verified'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ✓ Verified Data
                <span className="ml-2 text-xs">
                  ({students.filter(s => s.student_info.data_source === 'verified').length})
                </span>
              </button>
            </div>
          </div>

          {/* Search and University Filter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔍 Search Students
              </label>
              <input
                type="text"
                placeholder="Search by name, email, or university..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* University Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🎓 Filter by University
              </label>
              <select
                value={universityFilter}
                onChange={(e) => setUniversityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Universities</option>
                {getUniqueUniversities().map(university => (
                  <option key={university} value={university}>
                    {university}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || universityFilter || dataSourceFilter !== 'all') && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setUniversityFilter('');
                  setDataSourceFilter('all');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Students with Matches"
            value={filteredStats.totalStudentsWithMatches}
            color="blue"
            sublabel={dataSourceFilter !== 'all' ? `Total: ${stats.totalStudentsWithMatches}` : null}
          />
          <StatCard
            label="Total Matches"
            value={filteredStats.totalMatches}
            color="green"
            sublabel={dataSourceFilter !== 'all' ? `Total: ${stats.totalMatches}` : null}
          />
          {debug && (
            <>
              <StatCard
                label="Using Application Data"
                value={debug.data_source_breakdown.current}
                color="purple"
                sublabel="More recent than verification"
              />
              <StatCard
                label="Using Verified Data"
                value={debug.data_source_breakdown.verified}
                color="indigo"
                sublabel="Partner-verified or no app"
              />
            </>
          )}
        </div>

        {/* Debug Toggle */}
        {debug && (
          <div className="mb-6">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              {showDebug ? '▼' : '▶'} Debug Information
            </button>

            {showDebug && (
              <div className="mt-2 bg-gray-800 text-gray-100 p-4 rounded-lg font-mono text-sm">
                <pre>{JSON.stringify(debug, null, 2)}</pre>
              </div>
            )}
          </div>
        )}

        {/* Success/Error Messages */}
        {tokenSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800">{tokenSuccess}</p>
          </div>
        )}

        {/* Bulk Actions Panel */}
        {selectedStudents.size > 0 && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-blue-900">
                  {selectedStudents.size} student{selectedStudents.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => setSelectedStudents(new Set())}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Clear Selection
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowBulkTokens(!showBulkTokens)}
                  disabled={tokenGenerating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {showBulkTokens ? 'Hide' : 'Show'} Token Generator
                </button>
              </div>
            </div>

            {showBulkTokens && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">Bulk Token Generation</h3>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="flex-1">
                    <label className="block text-sm text-blue-800 mb-1">
                      Token Expiry (hours)
                    </label>
                    <input
                      type="number"
                      value={tokenExpiryHours}
                      onChange={(e) => setTokenExpiryHours(parseInt(e.target.value) || 720)}
                      min="1"
                      max="8760"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-blue-600 mt-1">
                      Default: 720 hours (30 days)
                    </p>
                  </div>
                  <button
                    onClick={generateBulkTokens}
                    disabled={tokenGenerating || selectedStudents.size === 0}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {tokenGenerating ? 'Generating...' : `Generate ${selectedStudents.size} Token${selectedStudents.size !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CSV Export Buttons */}
        {filteredStudents.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={exportToCSV}
                disabled={tokenGenerating}
                className="flex-1 sm:flex-none px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">
                  Export CSV with Tokens {selectedStudents.size > 0 ? `(${selectedStudents.size} selected)` : `(${filteredStudents.length} total)`}
                </span>
                <span className="sm:hidden">Export CSV</span>
              </button>
              <button
                onClick={exportBasicCSV}
                className="flex-1 sm:flex-none px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">
                  Export Basic CSV {selectedStudents.size > 0 ? `(${selectedStudents.size} selected)` : `(${filteredStudents.length} total)`}
                </span>
                <span className="sm:hidden">Basic CSV</span>
              </button>
              <div className="flex-1 sm:flex-none flex items-center">
                <button
                  onClick={toggleSelectAll}
                  className="px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium"
                >
                  {selectedStudents.size === filteredStudents.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Status Message */}
        {(dataSourceFilter !== 'all' || searchTerm || universityFilter) && students.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">
              <strong>Showing {filteredStudents.length} of {students.length} students</strong>
              {dataSourceFilter !== 'all' && (
                <span> using {dataSourceFilter === 'current' ? 'Application Data 📱' : 'Verified Data ✓'}</span>
              )}
              {searchTerm && (
                <span> matching "{searchTerm}"</span>
              )}
              {universityFilter && (
                <span> from {universityFilter}</span>
              )}
            </p>
          </div>
        )}

        {/* Students List */}
        {filteredStudents.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-gray-400 text-5xl mb-4">🎓</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No Students Found
            </h2>
            <p className="text-gray-600 mb-2">
              {searchTerm || universityFilter
                ? 'No students found matching your search criteria'
                : dataSourceFilter === 'all'
                  ? 'No students match any active recruiter requirements'
                  : `No students using ${dataSourceFilter === 'current' ? 'Application' : 'Verified'} data match any active recruiter requirements`
              }
            </p>
            {(searchTerm || universityFilter) && (
              <p className="text-gray-500 text-sm">
                Try adjusting your search term or filters.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredStudents.map((student) => (
              <div key={student.student_info.id} className="bg-white rounded-lg shadow-md border-2 border-purple-300">
                {/* Student Header */}
                <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    {/* Checkbox and Student Info */}
                    <div className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedStudents.has(student.student_info.id)}
                        onChange={() => toggleStudentSelection(student.student_info.id)}
                        className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {student.student_info.name}
                          <span className="ml-2 text-sm font-normal text-gray-500">(ID: {student.student_info.id})</span>
                        </h3>
                        <p className="text-sm text-gray-600">{student.student_info.email}</p>
                      </div>
                    </div>

                    {/* Badges and Actions Container */}
                    <div className="flex flex-col items-end gap-2">
                      {/* Data Source Badge */}
                      {student.student_info.data_source === 'current' ? (
                        <span className="px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full whitespace-nowrap">
                          📱 Using Application Data
                          {student.student_info.application_date && (
                            <span className="ml-1">({formatDate(student.student_info.application_date)})</span>
                          )}
                        </span>
                      ) : (
                        <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                          ✓ Using Verified Data
                          {student.student_info.verified_at && (
                            <span className="ml-1">({formatDate(student.student_info.verified_at)})</span>
                          )}
                        </span>
                      )}

                      {/* Admin Upload Badge */}
                      {student.student_info.is_admin_upload && (
                        <span
                          className="px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full whitespace-nowrap cursor-help"
                          title={student.student_info.verified_by && student.student_info.verified_at
                            ? `Uploaded by: ${getAdminName(student.student_info.verified_by)}\nDate: ${formatDate(student.student_info.verified_at)}\n${student.student_info.admin_upload_notes ? `Batch: ${formatBatchInfo(student.student_info.admin_upload_notes)}` : ''}`
                            : ''
                          }
                        >
                          Admin CSV Upload
                        </span>
                      )}

                      {/* Generate Token Button */}
                      <button
                        onClick={() => generateIndividualToken(student.student_info.id)}
                        disabled={tokenGenerating}
                        className="mt-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {tokenGenerating ? 'Generating...' : 'Generate Token'}
                      </button>
                    </div>
                  </div>

                  {/* Student Details with Field-Level Comparison */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">GPA:</span>{' '}
                      <span className="text-gray-900">{student.student_info.gpa}</span>
                      {student.student_info.data_source === 'current' &&
                       student.student_info.verified_data.gpa &&
                       student.student_info.verified_data.gpa !== student.student_info.gpa && (
                        <span className="ml-2 text-xs text-gray-500">
                          (Verified: {student.student_info.verified_data.gpa})
                        </span>
                      )}
                    </div>

                    <div>
                      <span className="font-medium text-gray-700">Major:</span>{' '}
                      <span className="text-gray-900">{student.student_info.major}</span>
                      {student.student_info.data_source === 'current' &&
                       student.student_info.verified_data.major &&
                       student.student_info.verified_data.major !== student.student_info.major && (
                        <span className="ml-2 text-xs text-gray-500">
                          (Verified: {student.student_info.verified_data.major})
                        </span>
                      )}
                    </div>

                    <div>
                      <span className="font-medium text-gray-700">Class Year:</span>{' '}
                      <span className="text-gray-900">{student.student_info.class_year}</span>
                      {student.student_info.data_source === 'current' &&
                       student.student_info.verified_data.class_year &&
                       student.student_info.verified_data.class_year !== student.student_info.class_year && (
                        <span className="ml-2 text-xs text-gray-500">
                          (Verified: {student.student_info.verified_data.class_year})
                        </span>
                      )}
                    </div>

                    <div>
                      <span className="font-medium text-gray-700">University:</span>{' '}
                      <span className="text-gray-900">{student.student_info.university}</span>
                      {student.student_info.data_source === 'current' &&
                       student.student_info.verified_data.university &&
                       student.student_info.verified_data.university !== student.student_info.university && (
                        <span className="ml-2 text-xs text-gray-500">
                          (Verified: {student.student_info.verified_data.university})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Admin Upload Details - only show for admin uploads */}
                  {student.student_info.is_admin_upload && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Uploaded By:</span>{' '}
                          <span className="text-gray-900">
                            {getAdminName(student.student_info.verified_by)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Upload Date:</span>{' '}
                          <span className="text-gray-900">
                            {formatDate(student.student_info.verified_at)}
                          </span>
                        </div>
                        {student.student_info.admin_upload_notes && (
                          <div className="md:col-span-2">
                            <span className="font-medium text-gray-700">Batch:</span>{' '}
                            <span className="text-xs text-gray-600">
                              {formatBatchInfo(student.student_info.admin_upload_notes)}
                            </span>
                          </div>
                        )}
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">University Source:</span>{' '}
                          <span className="text-gray-900">
                            {student.student_info.current_university_verified || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Matching Companies - Collapsible */}
                <div className="border-t border-gray-200">
                  <button
                    onClick={() => toggleStudentExpanded(student.student_info.id)}
                    className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="text-sm font-semibold text-gray-700">
                      Matching Companies ({student.match_count})
                    </h4>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        expandedStudents.has(student.student_info.id) ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {expandedStudents.has(student.student_info.id) && (
                    <div className="px-6 pb-4">
                      <div className="space-y-2">
                        {student.matching_organizations.map((org, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                            <div>
                              <div className="font-medium text-gray-900">{org.organization}</div>
                              <div className="text-xs text-gray-600">
                                {org.recruiter_name} ({org.recruiter_email})
                              </div>
                            </div>
                            <div className="text-right text-xs text-gray-600">
                              <div>Min GPA: {org.minimum_gpa}</div>
                              {org.approved ? (
                                <span className="text-green-600 font-medium">✓ Approved</span>
                              ) : (
                                <span className="text-yellow-600 font-medium">⏳ Pending</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStudentMatchesRDL;
